<template>
  <div v-if="tabs.length > 0" class="drawer-tabs">
    <div class="drawer-tabs__scroll">
      <div
        v-for="tab in visibleTabs"
        :key="tab.monitorId"
        class="drawer-tab"
        :class="{
          'drawer-tab--active': tab.monitorId === activeId,
          'drawer-tab--stale': tab.isStale,
          'drawer-tab--murphy': tab.origin === 'murphy',
        }"
        :style="tabStyle(tab)"
        :title="tab.isStale ? tab.title + ' (información fuera de línea)' : tab.title"
        @click="$emit('select', tab.monitorId)"
      >
        <span class="drawer-tab__dot" :style="{ background: tab.color || '#666' }"></span>
        <span class="drawer-tab__title">{{ tab.title }}</span>
        <span
          v-if="tab.pendingEvents > 0 && tab.monitorId !== activeId"
          class="drawer-tab__badge"
          :title="tab.pendingEvents + ' eventos no atendidos'"
        >{{ tab.pendingEvents > 9 ? '9+' : tab.pendingEvents }}</span>
        <span
          v-if="tab.isStale && tab.staleText && tab.monitorId !== activeId"
          class="drawer-tab__freshness"
          :title="'Última actualización: ' + tab.staleText"
        >{{ tab.staleText }}</span>
        <v-btn
          icon
          x-small
          class="drawer-tab__close"
          v-auto-tooltip="'Quitar pin'"
          @click.stop="$emit('unpin', tab.monitorId)"
        >
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <v-menu
      v-if="hasOverflow"
      v-model="menuOpen"
      offset-y
      left
      :close-on-content-click="false"
      content-class="drawer-tabs__menu"
      :nudge-bottom="4"
    >
      <template #activator="{ on, attrs }">
        <div
          class="drawer-tab drawer-tab--more"
          v-bind="attrs"
          v-on="on"
          :title="'Ver ' + overflowTabs.length + ' pin' + (overflowTabs.length === 1 ? '' : 'es') + ' mas'"
        >
          <v-icon x-small>mdi-dots-horizontal</v-icon>
          <span class="drawer-tab__title">+{{ overflowTabs.length }}</span>
        </div>
      </template>
      <div class="drawer-tabs__menu-list">
        <div
          v-for="tab in overflowTabs"
          :key="tab.monitorId"
          class="drawer-tabs__menu-item"
          :class="{ 'drawer-tabs__menu-item--stale': tab.isStale }"
          @click="onMenuSelect(tab.monitorId)"
        >
          <span class="drawer-tab__dot" :style="{ background: tab.color || '#666' }"></span>
          <span class="drawer-tabs__menu-title" :title="tab.title">{{ tab.title }}</span>
          <v-btn
            icon
            x-small
            class="drawer-tab__close"
            v-auto-tooltip="'Quitar pin'"
            @click.stop="$emit('unpin', tab.monitorId)"
          >
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
export default {
  name: 'MapDrawerTabs',
  props: {
    tabs: {
      type: Array,
      default() { return [] },
    },
    activeId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      menuOpen: false,
      // IDs de los pines visibles inline, en orden de display (izq→der).
      // Se mantiene estable: solo cambia cuando se agrega/remueve un pin
      // o cuando el activeId esta en overflow (se promueve al slot 0).
      visibleIds: [],
    }
  },
  watch: {
    // Reconciliar visibleIds con el array actual de tabs (pines)
    // cuando se agrega o elimina un pin.
    tabs: {
      immediate: true,
      handler(newTabs) {
        const tabs = newTabs || []
        const tabIds = tabs.map(t => t.monitorId)
        const tabIdsSet = {}
        tabIds.forEach(id => { tabIdsSet[id] = true })

        // Remover ids que ya no estan pineados
        const kept = this.visibleIds.filter(id => tabIdsSet[id])
        const keptSet = {}
        kept.forEach(id => { keptSet[id] = true })

        // Rellenar hasta maxVisible con pines no incluidos aun
        for (let j = 0; j < tabIds.length && kept.length < this.maxVisible; j++) {
          if (!keptSet[tabIds[j]]) {
            kept.push(tabIds[j])
            keptSet[tabIds[j]] = true
          }
        }

        // Si el activeId es un pin nuevo que cayo fuera de visibleIds,
        // promoverlo al slot 0 (usuario acaba de pinear algo que esta activo)
        if (this.activeId && tabIdsSet[this.activeId] && !kept.includes(this.activeId)) {
          if (kept.length >= this.maxVisible) {
            kept[0] = this.activeId
          } else {
            kept.unshift(this.activeId)
          }
        }

        this.visibleIds = kept
      },
    },

    // Cuando activeId cambia, si no esta visible lo promueve al slot 0
    // reemplazando el tab mas antiguo (leftmost). Mantiene posiciones
    // estables del resto — no hay reordenamiento espureo.
    activeId(newId) {
      if (!newId) return
      if (this.visibleIds.includes(newId)) return
      // Solo promover si es un tab valido (pineado)
      const isPinned = (this.tabs || []).some(t => t.monitorId === newId)
      if (!isPinned) return

      const updated = this.visibleIds.slice()
      if (updated.length < this.maxVisible) {
        updated.unshift(newId)
      } else {
        updated[0] = newId
      }
      this.visibleIds = updated
    },
  },
  computed: {
    maxVisible() {
      return 3
    },
    visibleTabs() {
      const tabs = this.tabs || []
      if (tabs.length <= this.maxVisible) return tabs
      // Construir indice monitorId → tab para lookup O(1)
      const byId = {}
      tabs.forEach(t => { byId[t.monitorId] = t })
      // Traducir visibleIds a tabs (filtrando los que ya no existen)
      return this.visibleIds.reduce((result, id) => {
        if (byId[id]) result.push(byId[id])
        return result
      }, [])
    },
    overflowTabs() {
      const visibleSet = {}
      this.visibleTabs.forEach(t => { visibleSet[t.monitorId] = true })
      return (this.tabs || []).filter(t => !visibleSet[t.monitorId])
    },
    hasOverflow() {
      return this.overflowTabs.length > 0
    },
  },
  methods: {
    onMenuSelect(monitorId) {
      this.$emit('select', monitorId)
      this.menuOpen = false
    },

    // Estilo inline de la tab: borde superior por origen + borde completo del
    // accent cuando está activa. El borde superior ocurre siempre (drawer
    // derecho con color del entityType; Murphy neutro) para que el operador
    // distinga de un vistazo qué tab corresponde a qué superficie.
    tabStyle(tab) {
      var style = {}
      if (tab.origin === 'murphy') {
        style.borderTopColor = 'var(--kln-text-muted, rgba(255,255,255,0.55))'
      } else if (tab.color) {
        style.borderTopColor = tab.color
      }
      if (tab.monitorId === this.activeId) {
        style.borderColor = tab.color || 'var(--kln-accent)'
        if (tab.origin === 'murphy') style.borderTopColor = 'var(--kln-text-muted, rgba(255,255,255,0.55))'
        else style.borderTopColor = tab.color || 'var(--kln-accent)'
      }
      return style
    },
  },
}
</script>

<style scoped>
.drawer-tabs {
  background: var(--kln-bg-surface);
  border-bottom: 1px solid var(--kln-border-dim);
  padding: 4px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.drawer-tabs__scroll {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 4px;
  overflow: hidden;
}

/* Tabs visibles inline: reparten el ancho disponible en partes iguales.
   Con maxVisible=3 cada chip ocupa ~33% del scroll container. El
   title dentro usa ellipsis adaptivo segun el ancho asignado. */
.drawer-tabs__scroll > .drawer-tab {
  flex: 1 1 0;
  min-width: 0;
}

.drawer-tab {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  height: 26px;
  padding: 0 4px 0 8px;
  background: var(--kln-border-dim);
  border: 1px solid transparent;
  border-top: 2px solid transparent;
  border-radius: 4px;
  font-size: 11px;
  color: var(--kln-text);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
  user-select: none;
  position: relative;
}

.drawer-tab:hover {
  background: var(--kln-border-normal);
}

.drawer-tab--active {
  background: var(--kln-accent-soft);
  border-color: var(--kln-accent);
}

.drawer-tab--stale {
  opacity: 0.55;
}

/* Tab Murphy: fondo ligeramente distinto para reforzar diferenciación de
   entidad viva (drawer derecho) vs consulta (Murphy). */
.drawer-tab--murphy {
  background: var(--kln-border-dim);
}

.drawer-tab--murphy:hover {
  background: var(--kln-border-normal);
}

/* Badge de eventos no atendidos (esquina superior derecha del tab). */
.drawer-tab__badge {
  position: absolute;
  top: -6px;
  right: -4px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  background: var(--kln-alert-danger, #ff5252);
  color: #fff;
  border-radius: 7px;
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 0 0 1px var(--kln-bg-surface, #1a1a1f);
}

/* Texto de antigüedad en esquina inferior derecha del tab. Solo visible
   cuando el tab está stale y no activo. */
.drawer-tab__freshness {
  font-size: 9px;
  color: var(--kln-text-dim);
  font-variant-numeric: tabular-nums;
  margin-left: 2px;
  flex-shrink: 0;
}

.drawer-tab__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.drawer-tab__title {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-tab__close {
  width: 16px !important;
  height: 16px !important;
  margin-left: 2px;
}

.drawer-tab--more {
  background: var(--kln-accent-soft);
  border-color: var(--kln-border-accent);
}

.drawer-tab--more:hover {
  background: var(--kln-accent-soft);
  border-color: var(--kln-accent);
}
</style>

<style>
/* No-scoped: v-menu renders content in a portal outside the component tree,
   so scoped selectors don't reach it. */
.drawer-tabs__menu {
  background: var(--kln-bg-surface) !important;
  border: 1px solid var(--kln-border-normal);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
  border-radius: 6px;
  max-height: 320px;
  overflow-y: auto;
  min-width: 220px;
}

.drawer-tabs__menu-list {
  padding: 4px;
}

.drawer-tabs__menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: var(--kln-text);
  cursor: pointer;
  transition: background 0.12s;
}

.drawer-tabs__menu-item:hover {
  background: var(--kln-border-normal);
}

.drawer-tabs__menu-item--stale {
  opacity: 0.55;
}

.drawer-tabs__menu-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
